/**
 * Why panel (Phase 41A, spec §4.2.1): the structured reason behind one KPI
 * result, on the trigger detail page and in a KPI Analytics row.
 *
 * Presentational only. It renders the API's `why` view model, which holds
 * allowlisted structured fields, declared codes and one free-text field (a
 * DEVIATION step's note)
 * (apps/api/src/lib/kpi-result-view.ts), plus the result's observation status
 * for the "Not observed" / "Can't assess" line. It reads no other field of a
 * result, so the only AI free text it shows is a DEVIATION step's short note,
 * which the API puts in `why` (owner decision, Oct 23, 2026). Renders nothing
 * for shape 'none' or a shape this web does not know (spec S16).
 */

import type { KpiWhy, KpiWhyChangeover, KpiWhyPpe } from '@guild/shared';
import {
  formatInTimeZone,
  hasWhyPanel,
  lowSeverityNoteApplies,
  noValueLabel,
  whyExceptionText,
  whyListsTimes,
  whyMoreText,
  whyRuleName,
  whyStepText,
  whySummary,
  whyViolationText,
} from '../../lib/kpi-format';
import { cn } from '../../lib/utils';

export interface KpiWhyPanelProps {
  why: KpiWhy;
  /** The result's stored observation status (`rowObservationStatus`). */
  observationStatus: string | null;
  thresholdStatus: string | null;
  /** The trigger's clip window start (ISO UTC); the clip times count from it. */
  clipStart: string | null;
  /** The org's IANA zone; undefined reads in the browser's zone. */
  timeZone: string | undefined;
}

const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wide text-gray-500';

/** Steps that need attention stand out; the order itself comes from the API. */
const STEP_DOT_COLORS: Record<string, string> = {
  DEVIATION: 'bg-red-500',
  SKIPPED: 'bg-amber-500',
  DONE: 'bg-emerald-500',
  NOT_VISIBLE: 'bg-gray-300',
};

export function KpiWhyPanel({
  why,
  observationStatus,
  thresholdStatus,
  clipStart,
  timeZone,
}: KpiWhyPanelProps) {
  if (!hasWhyPanel(why)) return null;

  // Spec D3: a saved result with no value says why ("Not observed" / "Can't assess")
  const statusLine =
    observationStatus === 'NOT_OBSERVED' || observationStatus === 'NOT_ASSESSABLE'
      ? noValueLabel(observationStatus)
      : null;
  const summary = whySummary(why);
  const clipStartLabel = clipStart
    ? formatInTimeZone(clipStart, timeZone, {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : '--';

  return (
    <div className="space-y-3 text-sm text-gray-700 [overflow-wrap:anywhere]">
      {statusLine && <p className="font-semibold text-gray-900">{statusLine}</p>}

      {summary && (
        <p>
          {summary.lead && (
            <>
              <strong className="font-semibold text-gray-900">{summary.lead.count}</strong>{' '}
              {summary.lead.label}
            </>
          )}
          {summary.lead && summary.details.length > 0 && ' · '}
          {summary.details.join(' · ')}
        </p>
      )}

      {why.shape === 'ppe' ? (
        <PpeWhy why={why} thresholdStatus={thresholdStatus} />
      ) : (
        <ChangeoverWhy why={why} />
      )}

      {whyListsTimes(why) && (
        <p className="text-xs text-gray-500">
          Times are minutes:seconds from the start of the clip
          {clipStartLabel !== '--' && ` (clip window starts ${clipStartLabel})`}.
        </p>
      )}
    </div>
  );
}

function PpeWhy({ why, thresholdStatus }: { why: KpiWhyPpe; thresholdStatus: string | null }) {
  const more = whyMoreText(why.omittedViolations);
  return (
    <>
      {why.rules.length > 0 && (
        <table className="w-full max-w-md text-xs">
          <caption className="sr-only">Checks by rule</caption>
          <thead className="text-gray-500">
            <tr>
              <th scope="col" className="py-1 pr-3 text-left font-medium">
                Rule
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">
                Compliant
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-medium">
                Violations
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Not assessable
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {why.rules.map((rule, index) => (
              <tr key={`${rule.rule}-${index}`}>
                <th scope="row" className="py-1 pr-3 text-left font-medium text-gray-900">
                  {whyRuleName(rule)}
                </th>
                <td className="py-1 pr-3 text-right tabular-nums">{countText(rule.compliant)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{countText(rule.violations)}</td>
                <td className="py-1 text-right tabular-nums">{countText(rule.notAssessable)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="space-y-1">
        <p className={SECTION_LABEL}>Violations</p>
        {why.violations.length === 0 ? (
          <p>No individual violation was listed.</p>
        ) : (
          <ul className="space-y-0.5">
            {why.violations.map((violation, index) => (
              <li key={index}>{whyViolationText(violation)}</li>
            ))}
            {more && <li className="text-gray-500">{more}</li>}
          </ul>
        )}
      </div>

      <div className="space-y-0.5 text-xs text-gray-500">
        <p>Status comes from the share of checks met, not from severity.</p>
        {lowSeverityNoteApplies(why, thresholdStatus) && (
          <p>Every violation listed in this run is low severity.</p>
        )}
      </div>
    </>
  );
}

function ChangeoverWhy({ why }: { why: KpiWhyChangeover }) {
  const moreSteps = whyMoreText(why.omittedSteps);
  const moreExceptions = whyMoreText(why.omittedExceptions);
  return (
    <>
      {why.steps.length > 0 && (
        <div className="space-y-1">
          <p className={SECTION_LABEL}>Steps</p>
          <ul className="space-y-0.5">
            {why.steps.map((step) => (
              <li key={step.position} className="flex items-baseline gap-2">
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full',
                    STEP_DOT_COLORS[step.status] ?? 'bg-gray-300'
                  )}
                />
                <span>
                  {whyStepText(step)}
                  {step.note && (
                    <span className="block text-gray-600">&ldquo;{step.note}&rdquo;</span>
                  )}
                </span>
              </li>
            ))}
            {moreSteps && <li className="pl-3.5 text-gray-500">{moreSteps}</li>}
          </ul>
        </div>
      )}

      {why.exceptions.length > 0 && (
        <div className="space-y-1">
          <p className={SECTION_LABEL}>Exceptions</p>
          <ul className="space-y-0.5">
            {why.exceptions.map((exception, index) => (
              <li key={index}>{whyExceptionText(exception)}</li>
            ))}
            {moreExceptions && <li className="text-gray-500">{moreExceptions}</li>}
          </ul>
        </div>
      )}
    </>
  );
}

/** A count, or "—" when the result did not record it. */
function countText(count: number | null): string {
  return count === null ? '—' : count.toLocaleString();
}

export default KpiWhyPanel;
